import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, EventEmitter, Input, IterableDiffers, OnChanges, Output, SimpleChanges, Type, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ListRow } from '../../../model/list-view-row';
import { ListViewColumnFactory } from '../../../service/factory/list-view-column-factory.service';
import { ListViewBase } from '../list-view.base';
import { DefaultPageSize, PagingEvent } from '../paging/paging.component';
import { LanguageService } from '../../../service/language.service';

@Component({
    selector: 'app-accordion-list-view',
    templateUrl: './accordion-list-view.component.html',
    styleUrls: ['./accordion-list-view.component.scss'],
    animations: [
        trigger('expandCollapse', [
            state('collapsed', style({ height: '0px', minHeight: '0', transform: 'scaleY(0)', transformOrigin: 'top' })),
            state('expanded', style({ height: '*', transform: 'scaleY(1)', transformOrigin: 'top' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccordionListViewComponent<TSource = any, TComponent extends { dataContext: TSource } = any>
  extends ListViewBase<TSource>
  implements OnChanges
{
  from: number = 0;
  to: number = DefaultPageSize;
  selectedItemsCount: number;
  shouldComponentRerender: boolean = true;

  @Input() accordionComponent: Type<TComponent>;
  @Input() form: FormGroup;
  @Input() hasMultiSelect: boolean;
  @Input() inputGenerator: (x: TSource) => Partial<TComponent>;
  @Input() faultyItems?: TSource[];

  @Output() selectionChanged = new EventEmitter<TSource[]>();

  constructor(
    columnFactory: ListViewColumnFactory,
    languageService: LanguageService,
    private readonly _cdr: ChangeDetectorRef,
    iterableDiffers: IterableDiffers
  ) {
    super(columnFactory, languageService, iterableDiffers);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['itemsSource']) {
      this.checkSelectedItems();
    }
  }

  clearSelection(): void {
    this.rows?.forEach(x => (x.selected = false)); // Warning: if something modifies "row.selected" directly, we do not currently detect it!
    if (this.selectedItemsCount) {
      this.selectedItemsCount = 0;
      this.selectionChanged.emit([]);
    }
  }

  checkSelectedItems() {
    const selectedItems = this.getSelectedItems();
    this.selectedItemsCount = selectedItems.length;
    this.selectionChanged.emit(selectedItems);
  }

  getSelectedItems(): TSource[] {
    return this.rows?.filter(i => i.selected).map(x => x.dataSource) || [];
  }

  getRowNumber(index: number): number {
    return this.from + index + 1;
  }

  pagingChanged(e: PagingEvent) {
    this.shouldComponentRerender = false;
    this.from = e.from;
    this.to = e.to;
    this._cdr.detectChanges();
    this.shouldComponentRerender = true;
  }

  onRowClicked(row: ListRow<TSource>, event: PointerEvent) {
    super.onRowClicked(row, event);
    row.expanded = !row.expanded;
  }

  generateInputData(row: ListRow<TSource>): Partial<TComponent> {
    return this.inputGenerator?.(row.dataSource) || ({ dataContext: row.dataSource } as Partial<TComponent>);
  }

  getRandomNumber(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // The property 'index' should uniquely identify a row
  trackByFunction(index: number, row: ListRow<TSource>): number {
    return index;
  }

  isErrorInRow(row: ListRow<TSource>): boolean {
    return (!row.expanded && this.faultyItems?.includes(row.dataSource)) || false;
  }
}
